import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * HlsVideo — Cloudinary-first video player.
 *
 * NOTE: Kept the "HlsVideo" filename/export for backwards compatibility with
 * every caller in the app, but internally this is now a plain HTML5 <video>
 * element that plays Cloudinary AI-optimised delivery URLs (f_auto + q_auto).
 * No more Video.js, no more m3u8, no more Qencode. Cloudinary picks the
 * optimal codec (AV1/HEVC/H.264/VP9) and bitrate per client automatically.
 *
 * Props (same surface as before so no callers need to change):
 *   - src (string): source URL (may be a Cloudinary "video/upload/…" URL
 *     which we rewrite with `f_auto,q_auto,vc_auto`, or any other URL which
 *     we forward untouched).
 *   - poster, className, muted, loop, playsInline, preload, autoplay, controls
 *   - postId (string): used by /api/watch/progress + /api/posts/{id}/qualify
 *   - resumeFrom (number): initial seek in seconds
 *   - trackProgress (bool): default true. Writes watch progress + qualifies.
 *   - onLoadedMetadata, onLoadedData, onFirstFrame, onClick
 */

// Set once any HlsVideo has been signup-gated by the server. All subsequent
// video mounts refuse to play until the user signs up. Persists across route
// changes and video swaps so the guest cannot bypass by tapping through.
let guestHardLocked = false;
if (typeof window !== "undefined") {
  window.addEventListener("tain:signup-gate", (e) => {
    if (e?.detail?.reason === "watchLimit") guestHardLocked = true;
  });
}

const CLOUDINARY_UPLOAD_RE = /^(https?:\/\/res\.cloudinary\.com\/[^/]+\/video\/upload\/)(?!.*(?:f_auto|q_auto))(.+)$/i;

/** Rewrite raw Cloudinary video URLs to include AI-optimised delivery
 * transforms. `f_auto` picks the best modern format per client, `q_auto`
 * picks the perceptual quality that keeps 1080p-equivalent fidelity at the
 * lowest bandwidth. Existing transforms are preserved by only injecting when
 * none of the auto flags are already present.
 */
export function cloudinaryDeliveryUrl(url) {
  if (!url || typeof url !== "string") return url;
  const m = url.match(CLOUDINARY_UPLOAD_RE);
  if (!m) return url;
  return `${m[1]}f_auto,q_auto/${m[2]}`;
}

const HlsVideo = forwardRef(function HlsVideo(
  {
    src,
    poster,
    className,
    muted,
    loop,
    playsInline = true,
    preload = "auto",
    autoplay = false,
    controls = true,
    postId,
    resumeFrom,
    trackProgress = true,
    onClick,
    onLoadedData,
    onLoadedMetadata,
    onFirstFrame,
    ...rest
  },
  ref,
) {
  const videoRef = useRef(null);
  const firstFrameFiredRef = useRef(false);
  const lastProgressPushRef = useRef(0);
  const qualifiedRef = useRef(false);
  const lockedRef = useRef(false);
  const [locked, setLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState({ cost: 0, remaining: 0, admin_whatsapp: "" });

  // Forward the underlying <video> element so callers can access .play/.pause/.muted/etc.
  useImperativeHandle(ref, () => videoRef.current, []);

  const deliveryUrl = cloudinaryDeliveryUrl(src);

  // Resume from stored position when metadata is ready.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || typeof resumeFrom !== "number" || resumeFrom <= 3) return;
    const onMeta = () => {
      const d = v.duration || 0;
      if (d > 0 && resumeFrom < d - 5) {
        try { v.currentTime = resumeFrom; } catch { /* ignore */ }
      }
    };
    v.addEventListener("loadedmetadata", onMeta, { once: true });
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [resumeFrom, deliveryUrl]);

  // Kick off autoplay when requested. Muted autoplay is universally allowed;
  // audible autoplay is retried after the user's first gesture upstream.
  useEffect(() => {
    if (!autoplay) return;
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => {
      // Refuse to play if the guest is hard-locked by the signup gate.
      if (guestHardLocked) {
        try { v.pause(); } catch { /* ignore */ }
        try { window.dispatchEvent(new CustomEvent("tain:signup-gate", { detail: { reason: "watchLimit" } })); } catch { /* ignore */ }
        return;
      }
      try { v.play().catch(() => {}); } catch { /* ignore */ }
    };
    if (v.readyState >= 2) tryPlay();
    else v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, [autoplay, deliveryUrl]);

  // Guest hard-lock guardian — once the signup-gate has fired for a guest,
  // every subsequent play attempt on any video is intercepted. This prevents
  // "close the modal + tap the video" bypasses.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => {
      if (guestHardLocked) {
        try { v.pause(); } catch { /* ignore */ }
        try { window.dispatchEvent(new CustomEvent("tain:signup-gate", { detail: { reason: "watchLimit" } })); } catch { /* ignore */ }
      }
    };
    v.addEventListener("play", onPlay);
    return () => v.removeEventListener("play", onPlay);
  }, [deliveryUrl]);

  // Watch-progress + qualification persistence.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !trackProgress || !postId) return;

    const pushProgress = async () => {
      const t = v.currentTime;
      const d = v.duration || 0;
      if (!t || !d) return;
      const now = Date.now();
      // 4 s throttle — tighter than the old 10 s so the credit-lock 402
      // fires faster on the Watch page's fullscreen viewer. Short videos
      // used to slip past the paywall because throttle > video length.
      if (now - lastProgressPushRef.current < 4_000) return;
      lastProgressPushRef.current = now;
      try {
        await api.post("/watch/progress", { post_id: postId, position: t, duration: d });
      } catch { /* silent */ }
      if (!qualifiedRef.current && !lockedRef.current) {
        try {
          const url = new URL(window.location.href);
          const ref = url.searchParams.get("ref") || undefined;
          const { data } = await api.post(`/posts/${postId}/qualify`, {
            watched_seconds: t, watched_pct: d ? t / d : 0, ref,
          });
          if (data?.qualified) qualifiedRef.current = true;
        } catch (err) {
          if (err?.response?.status === 402) {
            const dt = err.response.data?.detail || {};
            // Guest branch: server says signup_required — fire the SignupGate.
            if (dt.signup_required) {
              lockedRef.current = true;
              try { v.pause(); } catch { /* ignore */ }
              try {
                window.dispatchEvent(new CustomEvent("tain:signup-gate", { detail: { reason: "watchLimit" } }));
              } catch { /* ignore */ }
              return;
            }
            // Signed-in low-credit lock.
            lockedRef.current = true;
            const whatsapp = dt.admin_whatsapp || "";
            const lockPayload = {
              cost: Number(dt.cost || 0),
              remaining: Number(dt.remaining || 0),
              admin_whatsapp: whatsapp,
            };
            setLockInfo(lockPayload);
            setLocked(true);
            try {
              window.dispatchEvent(new CustomEvent("tain:credit-lock", { detail: lockPayload }));
            } catch { /* ignore */ }
            try { v.pause(); } catch { /* ignore */ }
            try {
              const { toast } = await import("sonner");
              toast.error(
                "To continue watching, Buy Credits from Tainapp on Whatsapp",
                {
                  duration: 10000,
                  action: whatsapp ? {
                    label: "Message admin",
                    onClick: () => window.open(whatsapp, "_blank", "noopener"),
                  } : undefined,
                },
              );
            } catch { /* silent */ }
          }
        }
      }
    };

    v.addEventListener("timeupdate", pushProgress);
    v.addEventListener("ended", pushProgress);
    v.addEventListener("pause", pushProgress);
    // Fire an eager qualify check the moment playback actually starts.
    // Critical for the Watch page fullscreen flow: if the user is
    // resuming past the admin watch threshold with zero credits, this
    // triggers the 402 credit lock BEFORE they get to watch the rest.
    const eagerQualify = () => {
      lastProgressPushRef.current = 0;   // unthrottle the next push
      pushProgress();
    };
    v.addEventListener("play", eagerQualify);
    v.addEventListener("playing", eagerQualify);
    return () => {
      v.removeEventListener("timeupdate", pushProgress);
      v.removeEventListener("ended", pushProgress);
      v.removeEventListener("pause", pushProgress);
      v.removeEventListener("play", eagerQualify);
      v.removeEventListener("playing", eagerQualify);
    };
  }, [postId, trackProgress, deliveryUrl]);

  // Fire onFirstFrame once the first frame has actually painted.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !onFirstFrame) return;
    const fire = () => {
      if (firstFrameFiredRef.current) return;
      firstFrameFiredRef.current = true;
      try { onFirstFrame(v); } catch { /* ignore */ }
    };
    v.addEventListener("playing", fire, { once: true });
    v.addEventListener("loadeddata", fire, { once: true });
    return () => {
      v.removeEventListener("playing", fire);
      v.removeEventListener("loadeddata", fire);
    };
  }, [onFirstFrame, deliveryUrl]);

  return (
    <div className={className} style={{ position: "relative" }}>
      <video
        ref={videoRef}
        src={deliveryUrl}
        poster={poster}
        muted={muted}
        loop={loop}
        playsInline={playsInline}
        preload={preload}
        controls={controls}
        onClick={onClick}
        onLoadedMetadata={onLoadedMetadata}
        onLoadedData={onLoadedData}
        style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
        {...rest}
      />
      {locked && (
        <div
          data-testid="video-credit-lock-overlay"
          style={{
            position: "absolute", inset: 0, background: "rgba(0,0,0,0.85)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            color: "white", padding: 24, textAlign: "center",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
          <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 6 }}>Out of Credits</div>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 14, maxWidth: 300 }}>
            To continue watching, <span style={{ fontWeight: 700 }}>Buy Credits from Tainapp on Whatsapp</span>
            {lockInfo.cost > 0 && ` — this costs ${lockInfo.cost} credit${lockInfo.cost === 1 ? "" : "s"}, you have ${lockInfo.remaining}.`}
          </div>
          {lockInfo.admin_whatsapp && (
            <a
              href={lockInfo.admin_whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "#10b981", color: "#000", padding: "10px 20px",
                borderRadius: 10, fontWeight: 900, textDecoration: "none",
              }}
            >
              Message admin on WhatsApp
            </a>
          )}
        </div>
      )}
    </div>
  );
});

export default HlsVideo;
